import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  HeaderBar,
  OrdersGrid,
  ConnectionBanner,
} from "../components/liveOrder";
import "../components/liveOrder/liveOrder.css";

import {
  loadOrders,
  updateOrder,
  clearOrders,
} from "../components/liveOrder/liveOrder.store";

import { useOrderPause } from "../context/OrderPauseContext";
import { useWebSocket } from "../context/WebSocketContext";

export default function LiveOrder() {
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [orders, setOrders] = useState([]);

  const { isPaused } = useOrderPause();
  const { wsConnected } = useWebSocket();
  const isPausedRef = useRef(isPaused);

  // ---------------- INITIAL LOAD ----------------
  useEffect(() => {
    // load existing orders from storage
    setOrders(loadOrders());

    const onStorage = (e) => {
      if (e.key === "mlk.orders") {
        setOrders(loadOrders());
      }
    };

    const onChanged = () => setOrders(loadOrders());

    window.addEventListener("storage", onStorage);
    window.addEventListener("mlk:orders_changed", onChanged);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("mlk:orders_changed", onChanged);
    };
  }, []); // ✅ IMPORTANT: EMPTY dependency array

  // Update ref when isPaused changes
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // ---------------- COUNTS ----------------
  const counts = useMemo(() => {
    const c = {
      pending: 0,
      accepted: 0,
      declined: 0,
      inProcess: 0,
      delivered: 0,
      all: 0,
    };

    for (const o of orders) {
      c[o.status] = (c[o.status] || 0) + 1;
    }

    c.all = orders.filter(
      (o) => o.status === "pending" || o.status === "inProcess"
    ).length;

    return c;
  }, [orders]);

  // ---------------- FILTER ----------------
  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();

    let list = [...orders].sort(
      (a, b) => new Date(b.placedAt) - new Date(a.placedAt)
    );

    list =
      status === "all"
        ? list.filter((o) => o.status === "pending" || o.status === "inProcess")
        : list.filter((o) => o.status === status);

    if (text) {
      list = list.filter(
        (o) =>
          String(o.id).includes(text) ||
          o.customer?.name?.toLowerCase().includes(text)
      );
    }

    return list.slice(0, 50);
  }, [orders, status, query]);

  // ---------------- HANDLERS ----------------
  const handleStatusUpdate = (orderId, updates) => {
    updateOrder(orderId, updates);
    setOrders(loadOrders());
  };

  const handleClearOrders = () => {
    clearOrders();
    setOrders([]);
  };

  // ---------------- RENDER ----------------
  return (
    <div className="lo-page">
      <ConnectionBanner wsConnected={wsConnected} />

      <HeaderBar
        status={status}
        onStatusChange={setStatus}
        query={query}
        onQueryChange={setQuery}
        counts={counts}
        onClear={handleClearOrders}
      />

      <OrdersGrid orders={filtered} onStatusUpdate={handleStatusUpdate} />
    </div>
  );
}
